import ActionTextIcon from "@/components/icons/ActionTextIcon";
import FileTypeIconWithFilename from "@/components/icons/FileTypeIconWithFilename";
import PopconfirmDeleteIcon from "@/components/icons/PopconfirmDeleteIcon";
import { UploadDragger } from "@/components/upload-dragger/UploadDragger";
import { paginationTableProps, tableStandardProps } from "@/utils/componentUtils";
import { formatFileSize, formatLocaleDateTime } from "@/utils/formatUtils";
import { Divider, Popconfirm, Table } from "antd";
import { ColumnsType } from "antd/lib/table";
import { RcFile } from "antd/lib/upload";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { generatePath } from "react-router";
import {
  useDeleteCommissionsLevelAttachment,
  useUploadCommissionsLevelAttachments,
} from "../../../../../../common/api/queries";
import { PageSizes, TableSizes } from "../../../../../../common/constants";
import { Permission } from "../../../../../../common/security/enums";
import { useHasPermissions } from "../../../../../auth/hooks";
import { COMMISSIONS_LEVEL_ROUTE_PATHS } from "../../../paths";
import { CommissionsSettingsLevel, CommissionsSettingsLevelAttachment } from "../../../types";

interface Props {
  level: CommissionsSettingsLevel;
}

const CommissionsLevelAttachmentsTableView = ({ level }: Props) => {
  const { t } = useTranslation();

  const uploadCommissionsLevelAttachments = useUploadCommissionsLevelAttachments();
  const deleteCommissionsLevelAttachment = useDeleteCommissionsLevelAttachment();

  const hasTopAgentCommissionsManagePermission = useHasPermissions(Permission.TOP_AGENT_COMMISSIONS_MANAGE);

  const handleAttachmentsUpload = (_: RcFile, fileList: RcFile[]) => {
    if (fileList.length > 0) {
      const formData = new FormData();
      fileList.forEach((file) => formData.append("files", file));
      fileList.splice(0, fileList.length);

      uploadCommissionsLevelAttachments.mutate({ id: level.id, object: formData });
    }

    return false;
  };

  const columns: ColumnsType<CommissionsSettingsLevelAttachment> = [
    {
      key: "createdAt",
      title: t("common.createdAt"),
      width: 180,
      render: (_, record) => formatLocaleDateTime(record.createdAt),
    },
    {
      key: "filename",
      title: t("common.filename"),
      ellipsis: { showTitle: false },
      render: (_, record) => (
        <FileTypeIconWithFilename contentType={record.file.contentType} filename={record.file.filename} ellipsis />
      ),
    },
    {
      key: "size",
      title: t("common.filesize"),
      width: 100,
      render: (_, record) => formatFileSize(record.file.size),
    },
    {
      key: "actions",
      align: "right",
      fixed: "right",
      width: 180,
      render: (_, record) => (
        <>
          <ActionTextIcon
            path={generatePath(COMMISSIONS_LEVEL_ROUTE_PATHS.attachment.to, { id1: level.id, id2: record.id })}
            target="_blank"
            icon="export"
            color="blue"
            text={t("common.open")}
          />

          {hasTopAgentCommissionsManagePermission && (
            <>
              <Divider orientation="vertical" />

              <Popconfirm
                title={t("common.deleteAttachmentConfirm")}
                icon={<PopconfirmDeleteIcon />}
                okText={t("common.yes")}
                cancelText={t("common.no")}
                okType="danger"
                onConfirm={() => deleteCommissionsLevelAttachment.mutate({ id1: level.id, id2: record.id })}
              >
                <ActionTextIcon icon="delete" color="red" text={t("common.delete")} />
              </Popconfirm>
            </>
          )}
        </>
      ),
    },
  ];

  return (
    <>
      <Table<CommissionsSettingsLevelAttachment>
        {...tableStandardProps()}
        columns={columns}
        scroll={{ x: TableSizes.SMALL }}
        dataSource={level.attachments}
        pagination={{ ...paginationTableProps, pageSize: PageSizes.SMALL, total: level.attachments?.length }}
      />

      {hasTopAgentCommissionsManagePermission && (
        <div
          className={classNames(
            "margin-bottom-small",
            level.attachments?.length ? "table-footer-margin" : "margin-top-medium",
          )}
        >
          <UploadDragger multiple showUploadList={false} beforeUpload={handleAttachmentsUpload} />
        </div>
      )}
    </>
  );
};

export default CommissionsLevelAttachmentsTableView;
